import { useMemo } from "react";
import type { Feature, LineString, Point } from "geojson";

import type { Alert, Direction, MapStop } from "../contract/types";
import { useBackend } from "../app/BackendProvider";
import { useResource } from "../app/useResource";
import { useNetworkId } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { usePolledResource, useLiveJourneys } from "../cockpit/hooks";

/** Tracés GeoJSON des lignes actives, dans les deux sens. */
export function useLineShapes(activeLineIds: string[], highlightLineId: string | null) {
  const backend = useBackend();
  const networkId = useNetworkId();
  const lines = useLines();
  const key = [...activeLineIds].sort().join(",");

  const res = useResource(
    async (signal) => {
      const pairs = activeLineIds.flatMap((lineId) =>
        ([0, 1] as Direction[]).map((direction) => ({ lineId, direction })),
      );
      const shapes = await Promise.all(
        pairs.map(({ lineId, direction }) =>
          backend.api
            .getLineShape(networkId, lineId, direction, signal)
            .catch(() => null),
        ),
      );
      return shapes.filter((s): s is NonNullable<typeof s> => s !== null);
    },
    [backend, networkId, key],
  );

  const features = useMemo<Feature<LineString>[]>(() => {
    return (res.data ?? []).map((shape) => ({
      type: "Feature",
      properties: {
        lineId: shape.lineId,
        direction: shape.direction,
        color: lines.color(shape.lineId),
        highlight: shape.lineId === highlightLineId,
        dimmed: highlightLineId !== null && shape.lineId !== highlightLineId,
      },
      geometry: shape.shape,
    }));
  }, [res.data, lines, highlightLineId]);

  // D-019 / ODbL : au moins un tracé affiché dérive d'OpenStreetMap
  // (`source ∈ {pfaedle, osm-relation}`) ⇒ attribution obligatoire sur la carte.
  const derivesFromOsm = useMemo(
    () => (res.data ?? []).some((s) => s.source === "pfaedle" || s.source === "osm-relation"),
    [res.data],
  );

  return { features, derivesFromOsm, loading: res.loading, error: res.error };
}

/** Arrêts de la zone visible, marqués « perturbé » selon les alertes. */
export function useMapStops(bbox: readonly [number, number, number, number], disruptedStopIds: Set<string>) {
  const backend = useBackend();
  const networkId = useNetworkId();
  // Arrondi grossier pour ne pas recharger à chaque micro-déplacement.
  const key = bbox.map((n) => n.toFixed(2)).join(",");

  const res = usePolledResource(
    (signal) => backend.api.getStops(networkId, { bbox }, signal),
    [backend, networkId, key],
    60_000,
  );

  const withCoords = useMemo<MapStop[]>(
    () =>
      (res.data ?? []).filter(
        // On ne dessine QUE les quais : la station parente (`isStation`,
        // `location_type = 1`) est un point de regroupement placé au milieu des
        // quais — soit, sur un arrêt de rue, « au milieu de la route ». Ses
        // quais enfants (un par sens) portent déjà l'info.
        (s): s is MapStop => s.lat !== null && s.lon !== null && !s.isStation,
      ),
    [res.data],
  );

  const features = useMemo<Feature<Point>[]>(
    () =>
      withCoords.map((stop) => ({
        type: "Feature",
        properties: {
          stopId: stop.id,
          name: stop.name,
          disrupted: disruptedStopIds.has(stop.id),
          // D-017 — couleur de branding de la 1re ligne desservant l'arrêt.
          color: stop.color ?? "#ffffff",
        },
        geometry: { type: "Point", coordinates: [stop.lon as number, stop.lat as number] },
      })),
    [withCoords, disruptedStopIds],
  );

  return { features, stops: withCoords };
}

/** Alertes GTFS-RT en lecture seule (aucune création). */
export function useAlerts(): { alerts: Alert[]; loading: boolean } {
  const backend = useBackend();
  const networkId = useNetworkId();
  const res = usePolledResource((signal) => backend.api.getAlerts(networkId, signal), [backend, networkId], 30_000);
  return { alerts: res.data?.alerts ?? [], loading: res.loading };
}

export { useLiveJourneys };
